import { useEffect, useCallback, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import useDrawStore from '@/stores/drawStore';
import { useCollaboration } from '@/hooks/useCollaboration';
import DrawCanvas from '@/components/draw/DrawCanvas';
import Toolbar from '@/components/draw/Toolbar';
import PropertiesPanel from '@/components/draw/PropertiesPanel';
import IconLibrary from '@/components/draw/IconLibrary';
import TemplateLibrary from '@/components/draw/TemplateLibrary';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  Save,
  Download,
  Upload,
  FileJson,
  FileImage,
  FileCode,
  FileText,
  Settings,
  Users,
  Wifi,
  WifiOff,
  Moon,
  Sun,
  Loader2,
  Menu,
  PenTool,
  Sparkles,
  LayoutTemplate,
  RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export default function DrawPage() {
  const { workspaceId, diagramId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [templateLibraryOpen, setTemplateLibraryOpen] = useState(false);
  const canvasContainerRef = useRef(null);
  const cursorThrottleRef = useRef(null);

  const {
    documentId,
    documentName,
    setDocumentName,
    isDirty,
    isDarkMode,
    toggleDarkMode,
    showIconLibrary,
    showProperties,
    toggleProperties,
    isConnected,
    collaborators,
    elements,
    getExportData,
    importData,
    loadDocument,
    markSaved,
    newDocument,
    undo,
    redo,
    copy,
    paste,
    cut,
    deleteSelected,
    selectAll,
    groupSelected,
    ungroupSelected,
    setActiveTool,
    zoomIn,
    zoomOut,
    toggleIconLibrary,
    clearCanvas,
  } = useDrawStore();

  // Real-time collaboration
  const { 
    emitCursorMove, 
    emitElementAdd, 
    emitElementUpdate, 
    emitElementDelete 
  } = useCollaboration(diagramId, user);

  // Throttled cursor movement
  const handleCursorMove = useCallback((cursor) => {
    if (cursorThrottleRef.current) return;
    cursorThrottleRef.current = setTimeout(() => {
      emitCursorMove(cursor);
      cursorThrottleRef.current = null;
    }, 50); // 20fps for cursor updates
  }, [emitCursorMove]);

  // Load diagram if diagramId provided
  useEffect(() => {
    if (diagramId && diagramId !== 'new') {
      loadDiagram(diagramId);
    } else {
      newDocument();
    }
  }, [diagramId]);

  const loadDiagram = async (id) => {
    try {
      const response = await api.get(`/workspaces/${workspaceId}/diagrams/${id}`);
      loadDocument(response.data);
    } catch (error) {
      console.error('Failed to load diagram:', error);
      toast.error('Erreur lors du chargement du diagramme');
    }
  };

  // Save diagram
  const saveDiagram = async () => {
    setSaving(true);
    try {
      const data = {
        name: documentName,
        elements,
        workspace_id: workspaceId,
      };

      let response;
      if (documentId) {
        response = await api.put(`/workspaces/${workspaceId}/diagrams/${documentId}`, data);
      } else {
        response = await api.post(`/workspaces/${workspaceId}/diagrams`, data);
      }

      markSaved(response.data.id, response.data.updated_at);
      toast.success('Diagramme sauvegardé');

      // Update URL if new document
      if (!documentId) {
        navigate(`/workspace/${workspaceId}/draw/${response.data.id}`, { replace: true });
      }
    } catch (error) {
      console.error('Failed to save diagram:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Export functions
  const exportAsJSON = () => {
    const data = getExportData();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    downloadBlob(blob, `${documentName}.json`);
    toast.success('Export JSON téléchargé');
  };

  const exportAsSVG = async () => {
    const canvas = document.querySelector('[data-testid="draw-canvas"]');
    if (!canvas) return;

    // Create SVG from canvas
    const width = canvas.width;
    const height = canvas.height;
    const dataUrl = canvas.toDataURL('image/png');
    
    // Create a basic SVG wrapper
    const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${width}" height="${height}">
  <image xlink:href="${dataUrl}" width="${width}" height="${height}"/>
</svg>`;
    
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    downloadBlob(blob, `${documentName}.svg`);
    toast.success('Export SVG téléchargé');
  };

  const exportAsPNG = async () => {
    const canvas = document.querySelector('[data-testid="draw-canvas"]');
    if (!canvas) return;

    canvas.toBlob((blob) => {
      if (blob) {
        downloadBlob(blob, `${documentName}.png`);
        toast.success('Export PNG téléchargé');
      }
    }, 'image/png');
  };

  const exportAsPDF = async () => {
    const canvas = document.querySelector('[data-testid="draw-canvas"]');
    if (!canvas) {
      toast.error('Canvas non trouvé');
      return;
    }

    try {
      toast.info('Génération du PDF en cours...');
      
      // Convert canvas to image
      const imgData = canvas.toDataURL('image/png');
      
      // Create PDF with appropriate dimensions
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      // Determine orientation
      const orientation = imgWidth > imgHeight ? 'landscape' : 'portrait';
      const pdf = new jsPDF({
        orientation,
        unit: 'px',
        format: [imgWidth, imgHeight],
      });
      
      // Add the canvas image
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      
      // Save
      pdf.save(`${documentName}.pdf`);
      toast.success('Export PDF téléchargé');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Erreur lors de l\'export PDF');
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Clear/Reset canvas
  const handleClearCanvas = () => {
    const currentElements = useDrawStore.getState().elements;
    
    if (currentElements.length === 0) {
      toast.info('Le canevas est déjà vide');
      return;
    }
    
    const confirmed = window.confirm('Êtes-vous sûr de vouloir réinitialiser le canevas ? Cette action supprimera tous les éléments.');
    
    if (confirmed) {
      useDrawStore.getState().clearCanvas();
      toast.success('Canevas réinitialisé');
    }
  };

  // Import
  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result);
        importData(data);
        toast.success('Fichier importé avec succès');
      } catch (error) {
        toast.error('Erreur lors de l\'import du fichier');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      const target = e.target;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;

      // Modifier keys
      const ctrl = e.ctrlKey || e.metaKey;
      const shift = e.shiftKey;

      // Shortcuts with Ctrl
      if (ctrl) {
        switch (e.key.toLowerCase()) {
          case 'z':
            e.preventDefault();
            if (shift) {
              redo();
            } else {
              undo();
            }
            break;
          case 'y':
            e.preventDefault();
            redo();
            break;
          case 's':
            e.preventDefault();
            saveDiagram();
            break;
          case 'c':
            e.preventDefault();
            copy();
            break;
          case 'v':
            e.preventDefault();
            paste();
            break;
          case 'x':
            e.preventDefault();
            cut();
            break;
          case 'a':
            e.preventDefault();
            selectAll();
            break;
          case 'd':
            e.preventDefault();
            // Duplicate
            break;
          case 'g':
            e.preventDefault();
            if (shift) {
              ungroupSelected();
            } else {
              groupSelected();
            }
            break;
          case 'k':
            e.preventDefault();
            toggleIconLibrary();
            break;
          case '=':
          case '+':
            e.preventDefault();
            zoomIn();
            break;
          case '-':
            e.preventDefault();
            zoomOut();
            break;
        }
        return;
      }

      // Simple shortcuts (no modifier)
      switch (e.key.toLowerCase()) {
        case 'v':
          setActiveTool('select');
          break;
        case 'h':
          setActiveTool('pan');
          break;
        case 'r':
          setActiveTool('rectangle');
          break;
        case 'c':
          setActiveTool('circle');
          break;
        case 'l':
          setActiveTool('line');
          break;
        case 'a':
          setActiveTool('arrow');
          break;
        case 't':
          setActiveTool('text');
          break;
        case 'p':
          setActiveTool('freehand');
          break;
        case 'e':
          setActiveTool('eraser');
          break;
        case 'delete':
        case 'backspace':
          deleteSelected();
          break;
        case 'escape':
          useDrawStore.getState().clearSelection();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className={cn(
      "h-screen flex flex-col",
      isDarkMode ? "dark bg-[#0f0f1a]" : "bg-gray-50"
    )}>
      {/* Top Bar */}
      <div className="h-14 border-b border-border bg-background flex items-center justify-between px-4">
        {/* Left: Back + Title */}
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/workspace/${workspaceId}`)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          
          <div className="flex items-center gap-2">
            <PenTool className="h-5 w-5 text-primary" />
            <span className="font-bold text-lg">LearSpace Draw</span>
          </div>

          <div className="h-6 w-px bg-border mx-2" />

          {/* Document Name */}
          {editingName ? (
            <Input
              autoFocus
              className="w-48 h-8"
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              onBlur={() => setEditingName(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setEditingName(false);
              }}
            />
          ) : (
            <button
              className="text-sm font-medium hover:text-primary transition-colors"
              onClick={() => setEditingName(true)}
            >
              {documentName}
              {isDirty && <span className="text-muted-foreground ml-1">•</span>}
            </button>
          )}
        </div>

        {/* Center: Collaborators */}
        <div className="flex items-center gap-2">
          {collaborators.length > 0 && (
            <div className="flex -space-x-2">
              {collaborators.slice(0, 5).map((collab, i) => (
                <div
                  key={collab.userId}
                  className="w-8 h-8 rounded-full bg-primary/20 border-2 border-background flex items-center justify-center text-xs font-medium"
                  style={{ zIndex: 5 - i }}
                  title={collab.name}
                >
                  {collab.name?.charAt(0).toUpperCase()}
                </div>
              ))}
              {collaborators.length > 5 && (
                <div className="w-8 h-8 rounded-full bg-muted border-2 border-background flex items-center justify-center text-xs">
                  +{collaborators.length - 5}
                </div>
              )}
            </div>
          )}
          
          <Badge variant={isConnected ? 'default' : 'secondary'} className="gap-1">
            {isConnected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
            {isConnected ? 'Connecté' : 'Hors ligne'}
          </Badge>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          {/* Templates Button */}
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => setTemplateLibraryOpen(true)}
            data-testid="templates-btn"
          >
            <LayoutTemplate className="h-4 w-4" />
            Templates
          </Button>

          {/* Icon Library Toggle */}
          <Button
            variant={showIconLibrary ? 'default' : 'outline'}
            size="sm"
            className="gap-2"
            onClick={toggleIconLibrary}
          >
            <Sparkles className="h-4 w-4" />
            Icônes DevOps
          </Button>

          {/* Clear Canvas Button */}
          <Button
            variant="outline"
            size="sm"
            className="gap-2 text-destructive hover:bg-destructive hover:text-destructive-foreground"
            onClick={handleClearCanvas}
            data-testid="clear-canvas-btn"
          >
            <RotateCcw className="h-4 w-4" />
            Réinitialiser
          </Button>

          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleDarkMode}
          >
            {isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>

          {/* Properties Toggle */}
          <Button
            variant={showProperties ? 'default' : 'ghost'}
            size="icon"
            onClick={toggleProperties}
          >
            <Settings className="h-5 w-5" />
          </Button>

          {/* Import */}
          <label>
            <input
              type="file"
              accept=".json"
              className="hidden"
              onChange={handleImport}
            />
            <Button variant="outline" size="sm" className="gap-2" asChild>
              <span>
                <Upload className="h-4 w-4" />
                Import
              </span>
            </Button>
          </label>

          {/* Export Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <Download className="h-4 w-4" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportAsJSON}>
                <FileJson className="h-4 w-4 mr-2" />
                JSON (LearSpace Draw)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportAsSVG}>
                <FileCode className="h-4 w-4 mr-2" />
                SVG (Vectoriel)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportAsPNG}>
                <FileImage className="h-4 w-4 mr-2" />
                PNG (Image)
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={exportAsPDF}>
                <FileText className="h-4 w-4 mr-2" />
                PDF (Document)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Save */}
          <Button
            onClick={saveDiagram}
            disabled={saving}
            className="gap-2"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Sauvegarder
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Toolbar */}
        <Toolbar />

        {/* Canvas */}
        <div className="flex-1 relative" ref={canvasContainerRef}>
          <DrawCanvas onCursorMove={handleCursorMove} />
        </div>

        {/* Right Panel: Icon Library or Properties */}
        {showIconLibrary && <IconLibrary />}
        {showProperties && !showIconLibrary && <PropertiesPanel />}
      </div>

      {/* Template Library Modal */}
      <TemplateLibrary 
        open={templateLibraryOpen} 
        onOpenChange={setTemplateLibraryOpen} 
      />
    </div>
  );
}
